import React, { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { getLibraryRoles, updateLibraryRoles } from './apiLibrary';
import { BRAND, personName } from './libraryConstants';

const label = (m) => `${personName(m) || m.email}${m.companyName ? ` (${m.companyName})` : ''}`;

// Chair / co-chair / admin: choose who can contribute and who can approve.
// The chair and co-chair are always approvers and aren't listed here.
const LibraryRoles = ({ groupId }) => {
    const [data, setData] = useState(null);
    const [contributors, setContributors] = useState([]);
    const [approvers, setApprovers] = useState([]);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null); // { severity, text }

    useEffect(() => {
        let cancelled = false;
        getLibraryRoles(groupId).then(res => {
            if (cancelled) return;
            if (res.error) { setMessage({ severity: 'error', text: res.error }); return; }
            setData(res);
            const byId = new Map(res.members.map(m => [m._id, m]));
            setContributors(res.contributors.map(id => byId.get(id)).filter(Boolean));
            setApprovers(res.approvers.map(id => byId.get(id)).filter(Boolean));
        });
        return () => { cancelled = true; };
    }, [groupId]);

    if (!data) {
        return message ? <Alert severity={message.severity}>{message.text}</Alert>
            : <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    }

    const options = data.members.filter(m => !data.chairs.includes(m._id));
    const chairs = data.members.filter(m => data.chairs.includes(m._id));

    const save = async () => {
        setSaving(true);
        setMessage(null);
        const res = await updateLibraryRoles(groupId, {
            contributors: contributors.map(m => m._id),
            approvers: approvers.map(m => m._id)
        });
        setSaving(false);
        setMessage(res.error ? { severity: 'error', text: res.error } : { severity: 'success', text: 'Roles saved.' });
    };

    const picker = (title, help, value, setValue) => (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND.primary }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{help}</Typography>
            <Autocomplete
                multiple
                options={options}
                value={value}
                onChange={(e, v) => { setValue(v); setMessage(null); }}
                getOptionLabel={label}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                renderTags={(v, getTagProps) => v.map((m, i) => <Chip size="small" label={label(m)} {...getTagProps({ index: i })} key={m._id} />)}
                renderInput={(params) => <TextField {...params} placeholder="Search committee members" />}
            />
        </Paper>
    );

    return (
        <Stack spacing={2}>
            <Typography color="text.secondary">
                {chairs.length ? `${chairs.map(label).join(' and ')} can always approve. ` : ''}
                Everyone else on the committee can browse and download published devices.
            </Typography>
            {picker('Contributors', 'Can add devices, upload files and submit them for review.', contributors, setContributors)}
            {picker('Approvers', 'Can also review, approve, publish and withdraw devices. Nobody approves their own submission.', approvers, setApprovers)}
            {message && <Alert severity={message.severity}>{message.text}</Alert>}
            <Box>
                <Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ bgcolor: BRAND.primary }}>
                    Save roles
                </Button>
            </Box>
        </Stack>
    );
};

export default LibraryRoles;
